"use client";

import { useEffect, useRef, useState } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { AuthProvider, useAuth } from "@/lib/auth";
import { useFetch, warmCache } from "@/lib/use-fetch";
import { onAccountChanged } from "@/lib/account";
import { Logo } from "@/components/logo";
import { CommandPalette, openCommandPalette, type Command } from "@/components/command-palette";
import { IdleTimeout } from "@/components/idle-timeout";
import { NotificationBell } from "@/components/notification-bell";
import { Footer } from "@/components/footer";
import { ToastProvider } from "@/components/toast";
import { ConfirmProvider } from "@/components/confirm";
import { PasswordConfirmProvider } from "@/components/password-confirm";
import { ThemeToggle } from "@/components/theme-toggle";
import type { Account } from "@/lib/types";

const ICONS: Record<string, string> = {
  dashboard:
    "M2.25 12l8.954-8.955c.44-.439 1.152-.439 1.591 0L21.75 12M4.5 9.75v10.125c0 .621.504 1.125 1.125 1.125H9.75v-4.875c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21h4.125c.621 0 1.125-.504 1.125-1.125V9.75",
  send: "M6 12L3.269 3.126A59.768 59.768 0 0121.485 12 59.77 59.77 0 013.27 20.876L5.999 12zm0 0h7.5",
  bulk: "M10.34 15.84c-.688-.06-1.386-.09-2.09-.09H7.5a4.5 4.5 0 110-9h.75c.704 0 1.402-.03 2.09-.09m0 9.18c.253.962.584 1.892.985 2.783.247.55.06 1.21-.463 1.511l-.657.38c-.551.318-1.26.117-1.527-.461a20.845 20.845 0 01-1.44-4.282m3.102.069a18.03 18.03 0 01-.59-4.59c0-1.586.205-3.124.59-4.59m0 9.18a23.848 23.848 0 018.835 2.535M10.34 6.66a23.847 23.847 0 008.835-2.535m0 0A23.74 23.74 0 0018.795 3m.38 1.125a23.91 23.91 0 011.014 5.395m-1.014 8.855c-.118.38-.245.754-.38 1.125m0 0a23.91 23.91 0 001.014-5.395m0-3.46c.495.413.811 1.035.811 1.73 0 .695-.316 1.317-.811 1.73",
  search: "M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z",
  inbox:
    "M2.25 13.5h3.86a2.25 2.25 0 012.012 1.244l.256.512a2.25 2.25 0 002.013 1.244h3.218a2.25 2.25 0 002.013-1.244l.256-.512a2.25 2.25 0 012.013-1.244h3.859m-19.5.338V18a2.25 2.25 0 002.25 2.25h15A2.25 2.25 0 0021.75 18v-4.162c0-.224-.034-.447-.1-.661L19.24 5.338a2.25 2.25 0 00-2.15-1.588H6.911a2.25 2.25 0 00-2.15 1.588L2.35 13.177a2.25 2.25 0 00-.1.661z",
  shield:
    "M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z",
  chart:
    "M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z",
  identification:
    "M15 9h3.75M15 12h3.75M15 15h3.75M4.5 19.5h15a2.25 2.25 0 002.25-2.25V6.75A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25v10.5A2.25 2.25 0 004.5 19.5zm6-10.125a1.875 1.875 0 11-3.75 0 1.875 1.875 0 013.75 0zm1.294 6.336a6.721 6.721 0 01-3.17.789 6.721 6.721 0 01-3.168-.789 3.376 3.376 0 016.338 0z",
  receipt:
    "M9 14.25l6-6m4.5-3.493V21.75l-3.75-1.5-3.75 1.5-3.75-1.5-3.75 1.5V4.757c0-1.108.806-2.057 1.907-2.185a48.507 48.507 0 0111.186 0c1.1.128 1.907 1.077 1.907 2.185zM9.75 9h.008v.008H9.75V9zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm4.125 4.5h.008v.008h-.008V13.5zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z",
  clock: "M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z",
  report:
    "M3.75 3v11.25A2.25 2.25 0 006 16.5h2.25M3.75 3h-1.5m1.5 0h16.5m0 0h1.5m-1.5 0v11.25A2.25 2.25 0 0118 16.5h-2.25m-7.5 0h7.5m-7.5 0l-1 3m8.5-3l1 3m0 0l.5 1.5m-.5-1.5h-9.5m0 0l-.5 1.5M9 11.25v1.5M12 9v3.75m3-6v6",
  key: "M15.75 5.25a3 3 0 013 3m3 0a6 6 0 01-7.029 5.912c-.563-.097-1.159.026-1.563.43L10.5 17.25H8.25v2.25H6v2.25H2.25v-2.818c0-.597.237-1.17.659-1.591l6.499-6.499c.404-.404.527-1 .43-1.563A6 6 0 1121.75 8.25z",
  code: "M17.25 6.75L22.5 12l-5.25 5.25m-10.5 0L1.5 12l5.25-5.25m7.5-3l-4.5 16.5",
  users:
    "M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z",
  template:
    "M8.25 6.75h12M8.25 12h12m-12 5.25h12M3.75 6.75h.007v.008H3.75V6.75zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zM3.75 12h.007v.008H3.75V12zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm-.375 5.25h.007v.008H3.75v-.008zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z",
  support:
    "M8.625 9.75a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H8.25m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H12m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0h-.375m-13.5 3.01c0 1.6 1.123 2.994 2.707 3.227 1.087.16 2.185.283 3.293.369V21l4.184-4.183a1.14 1.14 0 01.778-.332 48.294 48.294 0 005.83-.498c1.585-.233 2.708-1.626 2.708-3.228V6.741c0-1.602-1.123-2.995-2.707-3.228A48.394 48.394 0 0012 3c-2.392 0-4.744.175-7.043.513C3.373 3.746 2.25 5.14 2.25 6.741v6.018z"
};

const NAV: { section: string; items: { href: string; label: string; icon: keyof typeof ICONS }[] }[] = [
  { section: "Overview", items: [{ href: "/dashboard", label: "Dashboard", icon: "dashboard" }] },
  {
    section: "Messaging",
    items: [
      { href: "/sms/send", label: "Send SMS", icon: "send" },
      { href: "/sms/bulk", label: "Bulk SMS", icon: "bulk" },
      { href: "/sms/personalized", label: "Personalized SMS", icon: "identification" },
      { href: "/sms/scheduled", label: "Scheduled", icon: "clock" },
      { href: "/sms/inbox", label: "Inbox", icon: "inbox" }
    ]
  },
  {
    section: "Audience",
    items: [
      { href: "/contacts", label: "Contacts", icon: "users" },
      { href: "/templates", label: "Templates", icon: "template" }
    ]
  },
  {
    section: "Reports",
    items: [
      { href: "/sms/reports", label: "Delivery Reports", icon: "report" },
      { href: "/sms/jobs", label: "Campaigns", icon: "chart" },
      { href: "/sms/status", label: "Message Lookup", icon: "search" },
      { href: "/usage", label: "Usage", icon: "chart" }
    ]
  },
  {
    section: "Account",
    items: [
      { href: "/billing", label: "Billing", icon: "receipt" },
      { href: "/api-docs", label: "API", icon: "code" },
      { href: "/api-keys", label: "API Keys", icon: "key" },
      { href: "/suppression", label: "Opt-outs", icon: "shield" },
      { href: "/account", label: "My Account", icon: "identification" }
    ]
  },
  {
    section: "Help",
    items: [{ href: "/support", label: "Support", icon: "support" }]
  }
];

// Every nav destination is reachable from the ⌘K palette.
const COMMANDS: Command[] = NAV.flatMap((g) => g.items.map((i) => ({ href: i.href, label: i.label, group: g.section })));

function Icon({ name }: { name: keyof typeof ICONS }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.7} className="h-5 w-5 shrink-0">
      <path strokeLinecap="round" strokeLinejoin="round" d={ICONS[name]} />
    </svg>
  );
}

function CreditBadge({ collapsed }: { collapsed: boolean }) {
  const { data, reload } = useFetch<Account>("account", { intervalMs: 30000 });
  // Refresh immediately when a send/top-up fires the account:changed signal (ref keeps the
  // listener stable so we subscribe once).
  const reloadRef = useRef(reload);
  reloadRef.current = reload;
  useEffect(() => onAccountChanged(() => reloadRef.current()), []);
  const prepaid = data?.accountType !== "POSTPAID";
  // CreditBalance mirrors the package balance; any excess over it is spendable wallet credit.
  // So spendable = max(creditBalance, package balance).
  const balance = data ? (prepaid ? Math.max(data.creditBalance ?? 0, data.totalPackageBalance ?? 0) : null) : null;
  const low = prepaid && balance !== null && balance <= (data?.lowCreditThreshold ?? 0);

  if (collapsed) {
    return (
      <div className="flex justify-center py-2" title={balance === null ? "Credit …" : `${balance.toLocaleString()} credits`}>
        <span className={`h-2.5 w-2.5 rounded-full ${balance === null ? "bg-gray-300" : low ? "bg-danger-500" : "bg-success-500"}`} />
      </div>
    );
  }

  return (
    <div className="px-2">
      <Link
        href="/billing"
        className={`block rounded-xl border px-3 py-2.5 transition ${
          low ? "border-danger-200 bg-danger-50 hover:bg-danger-100 dark:border-danger-500/30 dark:bg-danger-500/10" : "border-brand-200 bg-brand-50 hover:bg-brand-100 dark:border-brand-500/30 dark:bg-brand-500/10"
        }`}
      >
        <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">{prepaid ? "SMS credit" : "Account"}</p>
        <p className={`text-xl font-bold tabular-nums ${low ? "text-danger-600 dark:text-danger-400" : "text-brand-700 dark:text-brand-300"}`}>
          {prepaid ? (balance === null ? "—" : balance.toLocaleString()) : "Postpaid"}
        </p>
        {low && <p className="mt-0.5 text-[11px] font-medium text-danger-600 dark:text-danger-400">Low balance — top up.</p>}
      </Link>
    </div>
  );
}

function Sidebar({
  collapsed = false,
  onToggleCollapse,
  onNavigate
}: {
  collapsed?: boolean;
  onToggleCollapse?: () => void;
  onNavigate?: () => void;
}) {
  const pathname = usePathname();
  const { user, logout } = useAuth();
  const initials = (user?.displayName || user?.username || "?").slice(0, 2).toUpperCase();

  return (
    <div className="flex h-full flex-col bg-white">
      {/* Plain brand header: logo + wordmark on the sidebar surface, no gradient. */}
      <div className={`flex border-b border-gray-200 py-4 ${collapsed ? "flex-col items-center gap-3 px-2" : "items-center gap-3 px-5"}`}>
        <span className="flex h-9 w-9 shrink-0 items-center justify-center">
          <Logo className="h-full w-full" />
        </span>
        {!collapsed && (
          <div className="min-w-0 flex-1 leading-tight">
            <p className="truncate text-sm font-bold tracking-tight text-gray-900">Hormuud SMS</p>
            <p className="text-xs text-gray-500">Customer Portal</p>
          </div>
        )}
        {/* Kept visible even in the collapsed rail (stacked): on a tablet the rail can't be
            expanded, so hiding these would make notifications and the theme toggle unreachable.
            The panel opens rightward — a right-aligned one would run off the left of the screen. */}
        <div className={collapsed ? "flex flex-col items-center gap-1" : "flex items-center gap-0.5"}>
          <NotificationBell align="left" />
          <ThemeToggle />
        </div>
      </div>

      <div className={`pt-3 ${collapsed ? "px-2" : "px-2"}`}>
        <button
          onClick={openCommandPalette}
          title="Search (Ctrl+K)"
          className={`flex w-full items-center rounded-lg border border-gray-200 bg-gray-50/60 text-sm text-gray-500 transition hover:border-gray-300 hover:text-gray-700 ${
            collapsed ? "justify-center p-2" : "gap-2 px-3 py-2"
          }`}
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} className="h-4 w-4 shrink-0">
            <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
          </svg>
          {!collapsed && (
            <>
              <span className="flex-1 text-left">Search…</span>
              <kbd className="rounded border border-gray-200 px-1.5 py-0.5 text-[10px] font-medium text-gray-400">⌘K</kbd>
            </>
          )}
        </button>
      </div>

      <div className="py-3">
        <CreditBadge collapsed={collapsed} />
      </div>

      <nav className={`flex-1 space-y-5 overflow-y-auto py-2 ${collapsed ? "px-2" : "px-3"}`}>
        {NAV.map((group) => (
          <div key={group.section}>
            {!collapsed && (
              <p className="px-3 pb-1.5 text-[11px] font-semibold uppercase tracking-wider text-gray-400">{group.section}</p>
            )}
            {collapsed && <div className="mx-2 mb-1.5 border-t border-gray-100" />}
            <div className="space-y-0.5">
              {group.items.map((item) => {
                const active = pathname === item.href;
                return (
                  <Link
                    key={item.href}
                    href={item.href}
                    onClick={onNavigate}
                    aria-current={active ? "page" : undefined}
                    title={collapsed ? item.label : undefined}
                    className={`group relative flex items-center rounded-lg text-sm font-medium transition-colors ${
                      collapsed ? "justify-center px-2 py-2.5" : "gap-3 px-3 py-2"
                    } ${
                      active
                        ? "bg-brand-50 text-brand-700 dark:bg-brand-500/15 dark:text-brand-300"
                        : "text-gray-600 hover:bg-gray-100 hover:text-gray-900"
                    }`}
                  >
                    {active && <span className="absolute inset-y-1.5 left-0 w-1 rounded-r-full bg-brand-600" />}
                    <span className={active ? "text-brand-600 dark:text-brand-300" : "text-gray-400 transition-colors group-hover:text-gray-500"}>
                      <Icon name={item.icon} />
                    </span>
                    {!collapsed && item.label}
                  </Link>
                );
              })}
            </div>
          </div>
        ))}
      </nav>

      {onToggleCollapse && (
        <div className={`hidden border-t border-gray-100 lg:block ${collapsed ? "px-2 py-2" : "px-3 py-2"}`}>
          <button
            onClick={onToggleCollapse}
            title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            className={`flex w-full items-center rounded-lg px-3 py-2 text-sm font-medium text-gray-500 transition hover:bg-gray-100 hover:text-gray-700 ${
              collapsed ? "justify-center" : "gap-3"
            }`}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} className={`h-5 w-5 transition-transform ${collapsed ? "rotate-180" : ""}`}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
            </svg>
            {!collapsed && "Collapse"}
          </button>
        </div>
      )}

      <div className={`border-t border-gray-100 ${collapsed ? "p-2" : "p-3"}`}>
        <div className={`flex items-center rounded-lg transition-colors hover:bg-gray-50 ${collapsed ? "justify-center p-1.5" : "gap-3 px-2 py-2"}`}>
          <span
            title={user?.displayName || user?.username || ""}
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-brand-500 to-brand-700 text-xs font-bold text-pure-white shadow-sm"
          >
            {initials}
          </span>
          {!collapsed && (
            <>
              <div className="min-w-0 flex-1 leading-tight">
                <p className="truncate text-sm font-medium text-gray-900">{user?.displayName || user?.username}</p>
                <p className="text-xs text-gray-500">Customer</p>
              </div>
              <button
                onClick={logout}
                title="Sign out"
                className="rounded-md p-1.5 text-gray-400 transition hover:bg-gray-100 hover:text-danger-600"
                aria-label="Sign out"
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.7} className="h-5 w-5">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 9V5.25A2.25 2.25 0 0013.5 3h-6a2.25 2.25 0 00-2.25 2.25v13.5A2.25 2.25 0 007.5 21h6a2.25 2.25 0 002.25-2.25V15M12 9l-3 3m0 0l3 3m-3-3h12.75" />
                </svg>
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  const { loading } = useAuth();
  const pathname = usePathname();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [isTablet, setIsTablet] = useState(false);

  // Once the session is confirmed, pre-load the static datasets shared across pages (sender IDs,
  // rates, templates, contact groups) into the shared cache. The first time the user opens Send,
  // Bulk, Personalized, etc. the data is already there, so those screens paint instantly.
  useEffect(() => {
    if (loading) return;
    warmCache(["auth/source-addrs", "sms/rates", "account/templates", "account/contact-groups"]);
  }, [loading]);

  useEffect(() => {
    try {
      setCollapsed(window.localStorage.getItem("sidebar-collapsed") === "1");
    } catch {
      /* ignore */
    }
  }, []);

  // Per-page browser tab title. Every page is a client component, so it can't export `metadata` —
  // instead derive the title from the nav we already have, which means it can never drift out of
  // sync with the routes. Longest matching href wins (so /sms/send beats a bare /sms prefix).
  useEffect(() => {
    const match = COMMANDS
      .filter((c) => pathname === c.href || pathname.startsWith(`${c.href}/`))
      .sort((a, b) => b.href.length - a.href.length)[0];
    document.title = match ? `${match.label} · Hormuud SMS` : "Hormuud SMS — Customer Portal";
  }, [pathname]);

  // Track the tablet band (md ≤ w < lg) so the sidebar can render as a permanent icon rail there.
  // A media query, not a width guess — it stays correct through rotation and window resizing.
  useEffect(() => {
    const mq = window.matchMedia("(min-width: 768px) and (max-width: 1023.98px)");
    const sync = () => setIsTablet(mq.matches);
    sync();
    mq.addEventListener("change", sync);
    return () => mq.removeEventListener("change", sync);
  }, []);

  function toggleCollapse() {
    setCollapsed((c) => {
      const next = !c;
      try {
        window.localStorage.setItem("sidebar-collapsed", next ? "1" : "0");
      } catch {
        /* ignore */
      }
      return next;
    });
  }

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50 text-sm text-gray-500">
        <Logo className="mr-3 h-6 w-6 animate-pulse" /> Loading…
      </div>
    );
  }

  // Tablets (md ≤ w < lg) get a permanent ICON RAIL rather than the phone drawer — there's plenty
  // of room for it, and hiding navigation behind a hamburger on an iPad wastes the screen. The rail
  // is always collapsed there (labels need lg to fit), so the user's own collapse choice only
  // applies from lg up.
  const railOnly = isTablet;
  const effectiveCollapsed = railOnly || collapsed;

  const railWidth = effectiveCollapsed ? "md:w-[4.75rem]" : "md:w-64";
  const contentPad = effectiveCollapsed ? "md:pl-[4.75rem]" : "md:pl-64";

  return (
    <div className="min-h-screen bg-gray-50">
      <aside className={`fixed inset-y-0 left-0 z-30 hidden border-r border-gray-200 transition-[width] duration-200 md:block ${railWidth}`}>
        {/* onToggleCollapse only from lg up: on a tablet the rail can't usefully expand. */}
        <Sidebar collapsed={effectiveCollapsed} onToggleCollapse={railOnly ? undefined : toggleCollapse} />
      </aside>

      {drawerOpen && (
        <div className="fixed inset-0 z-40 md:hidden">
          <div className="absolute inset-0 animate-fade-in bg-pure-black/50 backdrop-blur-sm" onClick={() => setDrawerOpen(false)} />
          <aside className="absolute inset-y-0 left-0 w-64 max-w-[85vw] animate-slide-in-right border-r border-gray-200 shadow-2xl">
            <Sidebar onNavigate={() => setDrawerOpen(false)} />
          </aside>
        </div>
      )}

      <div className={`flex min-h-screen flex-col transition-[padding] duration-200 ${contentPad}`}>
        <header className="sticky top-0 z-20 flex items-center gap-3 border-b border-gray-200 bg-white/80 px-4 py-3 backdrop-blur-md md:hidden">
          <button onClick={() => setDrawerOpen(true)} className="rounded-md p-1.5 text-gray-600 transition hover:bg-gray-100" aria-label="Open menu">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} className="h-6 w-6">
              <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5M3.75 17.25h16.5" />
            </svg>
          </button>
          <Logo className="h-7 w-7" />
          <span className="flex-1 text-sm font-bold text-gray-900">Hormuud SMS</span>
          <NotificationBell />
          <ThemeToggle />
        </header>

        {/* Padding ladder: small phones get their width back, desktops keep breathing room. */}
        <main className="flex-1 px-4 py-5 sm:px-6 sm:py-7 lg:px-8 lg:py-8">
          <div key={pathname} className="mx-auto max-w-7xl animate-slide-up">
            {children}
          </div>
        </main>
        <Footer />
      </div>

      <CommandPalette commands={COMMANDS} />
      <IdleTimeout />
    </div>
  );
}

export default function AppLayout({ children }: { children: React.ReactNode }) {
  return (
    <AuthProvider>
      <ToastProvider>
        <ConfirmProvider>
          <PasswordConfirmProvider>
            <Shell>{children}</Shell>
          </PasswordConfirmProvider>
        </ConfirmProvider>
      </ToastProvider>
    </AuthProvider>
  );
}
